// src/Dashboard/pages/user/Messaging/components/ComposeGroupMessageDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FaPaperPlane, FaTimes, FaUsers } from "react-icons/fa";
import { toast } from "react-toastify";

import { getAllGroupsMessage } from "../../../../../services/groupServices";

import { MESSAGE_SUBJECT_OPTIONS } from "../utils/messagingUtils";

const quillModules = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "link",
];

export default function ComposeGroupMessageDrawer({ isOpen, onClose, onSend }) {
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [sending, setSending] = useState(false);

  const [groupId, setGroupId] = useState("");
  const [subjectChoice, setSubjectChoice] = useState("");
  const [customSubject, setCustomSubject] = useState("");
  const [content, setContent] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    setGroupId("");
    setSubjectChoice("");
    setCustomSubject("");
    setContent("");

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const loadGroups = async () => {
      try {
        setLoadingGroups(true);

        const data = await getAllGroupsMessage();

        if (!cancelled) {
          setGroups(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error("Erreur chargement groupes message :", error);
        toast.error("Erreur lors de la récupération des groupes.");
      } finally {
        if (!cancelled) {
          setLoadingGroups(false);
        }
      }
    };

    loadGroups();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const finalSubject = useMemo(() => {
    return subjectChoice === "Autre (saisir)"
      ? customSubject.trim()
      : subjectChoice.trim();
  }, [customSubject, subjectChoice]);

  const canSubmit =
    !!groupId && !!finalSubject && !!content && content !== "<p><br></p>";

  if (!isOpen) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canSubmit) {
      toast.error("Veuillez renseigner le groupe, l’objet et le message.");
      return;
    }

    try {
      setSending(true);

      await onSend?.({
        group_id: groupId,
        subject: finalSubject,
        content,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120]">
      <div
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute inset-y-0 right-0 flex w-full max-w-4xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <header className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-4 dark:border-slate-800 sm:px-6">
          <div className="min-w-0">
            <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
              <FaUsers />
              Message groupé
            </p>

            <h2 className="truncate text-lg font-bold text-slate-900 dark:text-white sm:text-xl">
              Envoyer un message à un groupe
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 transition hover:bg-slate-200 disabled:opacity-60 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            <FaTimes />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <div className="space-y-5">
              <div>
                <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                  Groupe
                </label>

                {loadingGroups ? (
                  <div className="h-12 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
                ) : (
                  <select
                    value={groupId}
                    onChange={(event) => setGroupId(event.target.value)}
                    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  >
                    <option value="">Sélectionner un groupe</option>
                    {groups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.nom}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
                <div>
                  <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                    Objet
                  </label>

                  <select
                    value={subjectChoice}
                    onChange={(event) => {
                      setSubjectChoice(event.target.value);
                      if (event.target.value !== "Autre (saisir)") {
                        setCustomSubject("");
                      }
                    }}
                    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  >
                    <option value="">Sélectionner un objet</option>
                    {MESSAGE_SUBJECT_OPTIONS.map((subject) => (
                      <option key={subject} value={subject}>
                        {subject}
                      </option>
                    ))}
                  </select>
                </div>

                {subjectChoice === "Autre (saisir)" && (
                  <div>
                    <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                      Objet personnalisé
                    </label>

                    <input
                      type="text"
                      value={customSubject}
                      onChange={(event) => setCustomSubject(event.target.value)}
                      className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                      placeholder="Ex. : Rappel entraînement..."
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                  Message
                </label>

                <div className="overflow-hidden rounded-2xl border border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-950">
                  <ReactQuill
                    theme="snow"
                    value={content}
                    onChange={setContent}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Rédigez votre message groupé..."
                  />
                </div>
              </div>
            </div>
          </div>

          <footer className="border-t border-slate-200 bg-white/95 p-4 dark:border-slate-800 dark:bg-slate-900/95">
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={sending}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={sending || !canSubmit}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FaPaperPlane />
                {sending ? "Envoi..." : "Envoyer au groupe"}
              </button>
            </div>
          </footer>
        </form>
      </aside>
    </div>
  );
}